import React, { useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { Check, Star, Zap } from 'lucide-react';
import { AuthContext } from '../App';

const PricingPage = () => {
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const handleStart = (role) => {
    if (user) {
      const hasRole = (user.roles || [user.role]).includes(role);
      if (hasRole) {
        navigate(role === 'client' ? '/dashboard/client' : '/dashboard/professional');
      } else {
        navigate(`/add-role/${role}`);
      }
    } else {
      navigate('/auth', { state: { selectedRole: role } });
    }
  };

  const plans = [
    {
      role: 'client',
      label: 'Client',
      subtitle: 'For those seeking services',
      price: 'CHF 12',
      period: '/year',
      badge: null,
      highlighted: false,
      features: [
        'Unlimited job posts',
        'Chat with professionals',
        'Secure payments',
        'Browse service providers',
      ],
      cta: 'Start Now',
    },
    {
      role: 'professional',
      label: 'Service Provider',
      subtitle: 'For professionals offering services',
      price: 'CHF 99',
      period: '/year',
      badge: 'PRO',
      highlighted: true,
      features: [
        'Unlimited job applications',
        'Eligibility for a Helply badge',
        'Priority in search',
        'Direct messaging',
      ],
      cta: 'Start Now',
    },
  ];

  return (
    <div className="min-h-screen bg-gray-50 py-16 px-4">
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3" data-testid="pricing-heading">
            Helply Swiss Pricing Plans
          </h1>
          <p className="text-lg text-gray-500">
            Choose your yearly subscription
          </p>
        </div>

        {/* Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-3xl mx-auto">
          {plans.map((plan) => {
            const hasRole = user && (user.roles || [user.role]).includes(plan.role);
            return (
              <div
                key={plan.role}
                data-testid={`pricing-card-${plan.role}`}
                className={`relative rounded-2xl overflow-hidden border-2 transition-transform hover:-translate-y-1 ${
                  plan.highlighted
                    ? 'bg-gray-900 border-red-600 shadow-2xl'
                    : 'bg-white border-gray-200 shadow-md'
                }`}
              >
                {/* PRO Badge */}
                {plan.badge && (
                  <div className="absolute top-4 right-4">
                    <span className="bg-red-600 text-white text-xs font-bold px-3 py-1 rounded-full flex items-center gap-1">
                      <Star className="w-3 h-3" />
                      {plan.badge}
                    </span>
                  </div>
                )}

                <div className="p-8">
                  {/* Role label */}
                  <p className={`text-sm font-semibold uppercase tracking-widest mb-1 ${plan.highlighted ? 'text-red-400' : 'text-red-600'}`}>
                    {plan.label}
                  </p>
                  <p className={`text-sm mb-6 ${plan.highlighted ? 'text-gray-400' : 'text-gray-500'}`}>
                    {plan.subtitle}
                  </p>

                  {/* Price */}
                  <div className="mb-6">
                    <span className={`text-5xl font-extrabold ${plan.highlighted ? 'text-white' : 'text-gray-900'}`}>
                      {plan.price}
                    </span>
                    <span className={`text-base ml-1 ${plan.highlighted ? 'text-gray-400' : 'text-gray-500'}`}>
                      {plan.period}
                    </span>
                    <p className={`text-xs mt-1 ${plan.highlighted ? 'text-gray-400' : 'text-gray-500'}`}>
                      Billed yearly
                    </p>
                  </div>

                  {/* Features */}
                  <ul className="space-y-3 mb-8">
                    {plan.features.map((feature) => (
                      <li key={feature} className="flex items-center gap-3">
                        <span className={`flex-shrink-0 w-5 h-5 rounded-full flex items-center justify-center ${plan.highlighted ? 'bg-red-600' : 'bg-red-100'}`}>
                          <Check className={`w-3 h-3 ${plan.highlighted ? 'text-white' : 'text-red-600'}`} />
                        </span>
                        <span className={`text-sm ${plan.highlighted ? 'text-gray-300' : 'text-gray-700'}`}>
                          {feature}
                        </span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <button
                    data-testid={`pricing-cta-${plan.role}`}
                    onClick={() => handleStart(plan.role)}
                    className={`w-full py-3 rounded-xl font-semibold text-base transition-all flex items-center justify-center gap-2 ${
                      plan.highlighted
                        ? 'bg-red-600 hover:bg-red-700 text-white shadow-lg shadow-red-900/30'
                        : 'bg-gray-900 hover:bg-gray-800 text-white'
                    }`}
                  >
                    <Zap className="w-4 h-4" />
                    {hasRole ? 'Go to Dashboard' : plan.cta}
                  </button>

                  {hasRole && (
                    <p className="text-center text-xs mt-2 text-green-500 font-medium">
                      Active on your account
                    </p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Footer note */}
        <p className="text-center text-sm text-gray-500 mt-10">
          Secure payments via Worldline / Saferpay &nbsp;&middot;&nbsp; Cancel anytime
        </p>
      </div>
    </div>
  );
};

export default PricingPage;
